/* @flow */
// typecheck: ignore file
import { uniqueId } from 'lodash';
import { LoadingInput } from 'components/DecoratedInput.jsx';
import { CONSENT_CHARGE_AMOUNT } from 'constants/ParentalConsentConstants.js';
import React from 'react';
import BoxBody from 'components/BoxBody.jsx';
import BoxHeader from 'components/BoxHeader.jsx';
import Button from 'components/Button.jsx';
import CardInput from 'payments/components/CardInput.jsx';
import Checkbox from 'components/Checkbox.jsx';
import CurrentUser from 'auth/CurrentUser';
import EncryptionNotice from 'components/EncryptionNotice.jsx';
import FamilyActions from 'actions/FamilyActions';
import Field from 'components/Field.jsx';
import Input from 'components/Input.jsx';
import invariant from 'invariant';
import Label from 'components/Label.jsx';
import Link from 'components/Link.jsx';
import LinkConstants from 'constants/LinkConstants';
import ModalTrigger from 'components/ModalTrigger.jsx';
import ParentalConsentPersonalInfoModal from 'user/components/ParentalConsentPersonalInfoModal.jsx';
import SettingsStore from 'stores/SettingsStore';
import StripeAPI from 'lib/StripeAPI';
import ToastActions from 'actions/ToastActions';

type StripeCard = {
  number: { value: number },
  cvc: { value: number },
  expiry: { value: string },
  zip: { value: string },
};

type State = {
  card: ?StripeCard,
  loadingStripeScript: bool,
  name: string,
  nameError: bool | string,
  nameId: string,
  option: string,
  parentName: string,
  safetyPolicy: bool,
  safetyPolicyError: bool | string,
  submitting: bool,
};

class ParentalConsentCardView extends React.Component {
  props: {
    childId: number,
    onProceed: Function,
  };

  static propTypes = {
    childId: React.PropTypes.number.isRequired,
    onProceed: React.PropTypes.func.isRequired,
  };

  state: State = {
    card: null,
    loadingStripeScript: true,
    name: '',
    nameError: false,
    nameId: '',
    option: '',
    parentName: '',
    safetyPolicy: true,
    safetyPolicyError: false,
    submitting: false,
  };

  componentWillMount() {
    this.setState({ nameId: uniqueId('id-') });
  }

  componentDidMount() {
    this.setUserName();

    StripeAPI
      .setPublishableKey(SettingsStore.getStripeKey())
      .then(this.handleStripeScriptLoaded, this.handleStripeScriptError);
  }

  setUserName = () => {
    this.setState({ name: CurrentUser.getUser().getFullName() });
  };

  handleStripeScriptError = () => {
    ToastActions.showErrorToast(`
      There was an error. Please try a different method, or email
      ${LinkConstants.SUPPORT_EMAIL}
      for help.
    `);
  };

  handleStripeScriptLoaded = () => {
    this.setState({ loadingStripeScript: false });
  };

  handleNameBlur = (event: SyntheticEvent) => {
    // $FlowFixMe
    const nameValid = event.target.checkValidity();

    if (nameValid) {
      this.setState({ nameError: false });
    } else {
      this.setState({ nameError: 'Card owner’s name is required' });
    }
  };

  handleNameChange = (event: SyntheticEvent) => {
    this.setState({
      // $FlowFixMe
      name: event.target.value,
    });
  };

  handleNameFocus = () => {
    this.setState({ nameError: false });
  };

  handleCardInvalid = () => {
    this.setState({ card: null });
  };

  handleCardValid = (card: Object) => {
    this.setState({ card });
  };

  handleSafetyPolicyChange = () => {
    const value = !this.state.safetyPolicy;

    this.setState({ safetyPolicy: value });

    if (value) {
      this.setState({ safetyPolicyError: false });
    } else {
      this.setState({ safetyPolicyError: 'Permission is required' });
    }
  };

  checkValidity = () => {
    return (
      this.state.name !== '' &&
      this.state.card !== null &&
      this.state.safetyPolicy
    );
  };

  handlePayment = (event: SyntheticEvent) => {
    event.preventDefault();

    const onSuccess = () => {
      ToastActions.showSuccessToast('Transaction successful');
      this.setState({ submitting: false });
      this.props.onProceed();
    };

    const onError = () => {
      ToastActions.showErrorToast(`
        There was an error with your transaction. Please try again or email
        ${LinkConstants.SUPPORT_EMAIL}
        for help.
      `);
      this.setState({ submitting: false });
    };

    this.setState({ submitting: true });

    invariant(this.state.card, 'ParentalConsentCardView tried to do some shit with a card but it was null!');

    StripeAPI.createCardToken({
      number: this.state.card.number.value,
      cvc: this.state.card.cvc.value,
      exp: this.state.card.expiry.value,
      address_zip: this.state.card.zip.value,
    }, (status, response) => {
      if (response.error) {
        ToastActions.showErrorToast(response.error.message);
      } else {
        FamilyActions
          .sendConsentPayment(this.props.childId, response.id, this.state.name)
          .then(onSuccess, onError);
      }
    });
  };

  renderCardInput = () => {
    const label = 'Debit or credit card';

    if (this.state.loadingStripeScript) {
      return (
        <div>
          <Label>{label}</Label>
          <LoadingInput />
        </div>
      );
    }

    return (
      <CardInput
        label={label}
        placeholder="Enter your debit or credit card"
        onInvalid={() => this.handleCardInvalid()}
        onValid={card => this.handleCardValid(card)}
      />
    );
  };

  render() {
    const { name, nameError, nameId, safetyPolicy, safetyPolicyError, submitting } = this.state;
    const safetyPolicyLabel = (
      <span>
        I’ve read and understand how
        {' '}
        <ModalTrigger modal={<ParentalConsentPersonalInfoModal />}>
          <Link>Remind uses personal information</Link>
        </ModalTrigger>.
        {' '}
        I give my child permission to use Remind.
      </span>
    );

    return (
      <section className="ParentalConsentCardView">
        <BoxHeader
          title="Verify your identity with a card"
          subtext={`${CONSENT_CHARGE_AMOUNT} will be charged to your card. We’ll refund it soon!`}
        />
        <BoxBody>
          <form onSubmit={(event) => this.handlePayment(event)}>
            <Field error={nameError}>
              <Label htmlFor={nameId}>Name</Label>
              <Input
                id={nameId}
                placeholder="Enter your name as it appears on your card"
                required
                value={name}
                onBlur={event => this.handleNameBlur(event)}
                onChange={event => this.handleNameChange(event)}
                onFocus={event => this.handleNameFocus(event)}
              />
            </Field>
            <Field>
              {this.renderCardInput()}
            </Field>
            <Field error={safetyPolicyError}>
              <Checkbox
                label={safetyPolicyLabel}
                defaultChecked={safetyPolicy}
                required
                onChange={event => this.handleSafetyPolicyChange(event)}
              />
            </Field>
            <Button
              type="submit"
              disabled={!this.checkValidity()}
              full
              loading={submitting}>
              Submit
            </Button>
            <EncryptionNotice />
          </form>
        </BoxBody>
      </section>
    );
  }
}

export default ParentalConsentCardView;



// WEBPACK FOOTER //
// ./src/user/components/ParentalConsentCardView.jsx